import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useTheme, applyTheme } from "@/context/ThemeContext";
import { PageHeader } from "@/components/ui-kit";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Logo } from "@/components/Logo";
import { SectionsManager } from "@/components/ClassPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Palette } from "lucide-react";

const PRESETS = ["#6D28D9", "#4F46E5", "#0F766E", "#BE185D", "#C2410C", "#1D4ED8"];
const ACCENT_PRESETS = ["#F59E0B", "#EAB308", "#F97316", "#EF4444", "#10B981", "#06B6D4", "#EC4899"];
const DARK_PRESETS = ["#130F26", "#0F172A", "#111827", "#0B1F1A", "#1F0D1A", "#0A1A2F", "#1C1917"];

export default function SettingsPage() {
  const { theme, setTheme, refresh } = useTheme();
  const [f, setF] = useState(theme);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(theme); }, [theme]);
  useEffect(() => { applyTheme(f); }, [f]);
  useEffect(() => () => { refresh(); }, [refresh]);

  async function save() {
    setSaving(true);
    try { const { data } = await api.put("/settings/theme", f); setTheme(data); toast.success("Theme saved for everyone"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Settings" title="Theme & Branding" subtitle="Changes apply to every user's app after saving." />
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>School name</Label><Input data-testid="theme-school-name" value={f.school_name} onChange={(e) => setF({ ...f, school_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Tagline</Label><Input data-testid="theme-tagline" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="mt-1" /></div>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Palette size={14} /> Main colour</Label>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {PRESETS.map((c) => <button key={c} data-testid={`preset-${c.slice(1)}`} onClick={() => setF({ ...f, primary_color: c })} className={`h-9 w-9 rounded-full ring-offset-2 ${f.primary_color === c ? "ring-2 ring-slate-900" : ""}`} style={{ background: c }} />)}
              <input data-testid="theme-primary" type="color" value={f.primary_color} onChange={(e) => setF({ ...f, primary_color: e.target.value.toUpperCase() })} className="h-9 w-12 rounded cursor-pointer" />
            </div>
          </div>
          <div>
            <Label>Accent colour (logo badge)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {ACCENT_PRESETS.map((c) => <button key={c} type="button" data-testid={`accent-preset-${c.slice(1)}`} onClick={() => setF({ ...f, accent_color: c })} className={`h-9 w-9 rounded-full ring-offset-2 ${f.accent_color?.toUpperCase() === c ? "ring-2 ring-slate-900" : ""}`} style={{ background: c }} />)}
              <input data-testid="theme-accent" type="color" value={f.accent_color} onChange={(e) => setF({ ...f, accent_color: e.target.value.toUpperCase() })} className="h-9 w-12 rounded cursor-pointer" />
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div><div className="font-semibold text-slate-900 text-sm">Dark look for admins</div><div className="text-xs text-slate-500">Master Admin and Super Admin screens use the dark theme</div></div>
              <Switch data-testid="theme-admin-dark" checked={f.admin_dark} onCheckedChange={(v) => setF({ ...f, admin_dark: v })} />
            </div>
            {f.admin_dark && (
              <div>
                <Label>Dark panel colour</Label>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {DARK_PRESETS.map((c) => <button key={c} type="button" data-testid={`dark-preset-${c.slice(1)}`} onClick={() => setF({ ...f, dark_color: c })} className={`h-9 w-9 rounded-full ring-offset-2 border border-white/20 ${(f.dark_color || "#130F26").toUpperCase() === c ? "ring-2 ring-slate-900" : ""}`} style={{ background: c }} />)}
                  <input data-testid="theme-dark-color" type="color" value={f.dark_color || "#130F26"} onChange={(e) => setF({ ...f, dark_color: e.target.value.toUpperCase() })} className="h-9 w-12 rounded cursor-pointer" />
                </div>
                <div className="mt-3 rounded-xl p-3 flex gap-2" style={{ background: "var(--dark-bg)" }}>
                  <div className="flex-1 rounded-lg p-2 text-xs text-white" style={{ background: "var(--dark-surface)" }}>Panel</div>
                  <div className="flex-1 rounded-lg p-2 text-xs text-white" style={{ background: "var(--dark-raised)" }}>Tile</div>
                </div>
              </div>
            )}
          </div>
          <PhotoUpload label="School logo" value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} />
          <Button data-testid="theme-save" onClick={save} disabled={saving} className="w-full btn-brand">Save theme</Button>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-sm">
          <div className="brand-header hero-curve p-8 text-white"><Logo onDark size="lg" stacked /><div className="text-center mt-4 font-display text-xl font-bold">Preview</div></div>
          <div className="bg-white p-5 text-sm text-slate-600">Buttons, headers and menus use your main colour. The logo uses your uploaded file, or the built-in mark if none.</div>
        </div>
        <div className="lg:col-span-2"><SectionsManager /></div>
      </div>
    </AppShell>
  );
}
